/* @jsx h */
// @ts-nocheck
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  Teleport,
  watch,
} from "vue";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue";
import { LemmonModal } from "../Modal";
import { NumPad } from "../NumPad";

interface FastNumberInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  min?: number;
  disabled?: boolean;
  disableResponsive?: boolean;
  showInModal?: boolean;
  isSmall?: boolean;
  /** Render as a compact label+value row instead of the full button */
  simplified?: boolean;
  /** Label shown in simplified mode when no value is set */
  simplifiedLabel?: string;
  /** Helper text shown in simplified mode when no value is set */
  simplifiedHelperText?: string;
  /** Additional classes applied to the trigger button */
  class?: string;
}

export const FastNumberInput = defineComponent<FastNumberInputProps>({
  name: "FastNumberInput",
  props: {
    value: {
      type: Number as PropType<number | null>,
      default: null,
    },
    onChange: {
      type: Function as PropType<(value: number | null) => void>,
      required: true,
    },
    placeholder: {
      type: String,
      default: "Enter number",
    },
    min: {
      type: Number,
      default: 1,
    },
    disabled: Boolean,
    disableResponsive: Boolean,
    showInModal: Boolean,
    isSmall: Boolean,
    simplified: Boolean,
    simplifiedLabel: String,
    simplifiedHelperText: String,
    class: String,
  },
  setup(props) {
    const isOpen = ref(false);
    const draftValue = ref("");
    const referenceEl = ref<HTMLElement | null>(null);
    const floatingEl = ref<HTMLElement | null>(null);
    const isCompactViewport = ref(false);

    const { floatingStyles, update } = useFloating(referenceEl, floatingEl, {
      placement: "left-start",
      whileElementsMounted: autoUpdate,
      middleware: [offset(12), flip(), shift({ padding: 12 })],
    });

    const displayValue = computed(() => (props.value != null && props.value >= props.min ? String(props.value) : ""));
    const shouldUseModal = computed(() => props.showInModal || (!props.disableResponsive && isCompactViewport.value));

    const sanitizeDecimal = (raw: string): string => {
      const cleaned = raw.replace(/[^\d.]/g, "");
      const [integer, ...rest] = cleaned.split(".");
      return rest.length > 0 ? `${integer}.${rest.join("")}` : integer;
    };

    const normalizeValue = (rawValue: string) => {
      const cleaned = sanitizeDecimal(rawValue);
      if (!cleaned || cleaned === ".") return null;
      const parsed = parseFloat(cleaned);
      if (Number.isNaN(parsed) || parsed < props.min) return null;
      return parsed;
    };

    const open = async () => {
      if (props.disabled) return;
      draftValue.value = displayValue.value;
      isOpen.value = true;
      await nextTick();
      update();
    };

    const close = () => {
      isOpen.value = false;
    };

    const handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
      if (shouldUseModal.value) {
        return;
      }

      const target = event.target as Node | null;
      if (!target) return;

      if (referenceEl.value?.contains(target) || floatingEl.value?.contains(target)) {
        return;
      }

      close();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    watch(
      () => props.disabled,
      (disabled) => {
        if (disabled) close();
      },
    );

    watch(
      () => props.value,
      (value) => {
        if (isOpen.value) return;
        draftValue.value = value != null && value >= props.min ? String(value) : "";
      },
      { immediate: true },
    );

    const submitValue = () => {
      props.onChange(normalizeValue(draftValue.value));
      close();
    };

    const updateViewport = () => {
      isCompactViewport.value = window.innerWidth < 750;
    };

    onMounted(() => {
      updateViewport();
      document.addEventListener("mousedown", handleOutsidePointer);
      document.addEventListener("touchstart", handleOutsidePointer);
      document.addEventListener("keydown", handleEscape);
      window.addEventListener("resize", updateViewport);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("mousedown", handleOutsidePointer);
      document.removeEventListener("touchstart", handleOutsidePointer);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateViewport);
    });

    return () => {
      if (props.simplified) {
        return (
          <div class="relative w-full">
            <div ref={referenceEl}>
              <button
                type="button"
                class={`flex w-full items-center justify-between gap-2 rounded-lg border border-base-300 px-3 py-2 text-left transition-all hover:border-primary ${
                  isOpen.value ? "border-primary" : ""
                } disabled:cursor-not-allowed disabled:opacity-40 ${props.class || ""}`}
                onClick={isOpen.value ? close : open}
                disabled={props.disabled}
              >
                <span class="text-xs font-medium uppercase tracking-wide">
                  {props.simplifiedLabel || props.placeholder}
                </span>
                <span
                  class={`text-sm font-semibold ${displayValue.value ? "text-base-content" : "text-base-content/30"}`}
                >
                  {displayValue.value || "—"}
                </span>
              </button>
            </div>

            {isOpen.value && shouldUseModal.value ? (
              <LemmonModal title={props.simplifiedLabel || props.placeholder || ""} onClose={close}>
                <div class="space-y-4 p-4">
                  <div class="mx-auto w-full max-w-[400px]">
                    <NumPad
                      value={draftValue.value}
                      onChange={(value) => {
                        draftValue.value = sanitizeDecimal(value);
                      }}
                    />
                  </div>
                  <button type="button" class="btn btn-accent h-11 w-full rounded-xl" onClick={submitValue}>
                    Confirm
                  </button>
                </div>
              </LemmonModal>
            ) : null}

            {isOpen.value && !shouldUseModal.value ? (
              <Teleport to="body">
                <div
                  ref={floatingEl}
                  style={floatingStyles.value}
                  class="z-[1200] w-[380px] overflow-hidden rounded-[24px] border border-base-content/10 bg-base-100/96 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur"
                >
                  <NumPad
                    value={draftValue.value}
                    onChange={(value) => {
                      draftValue.value = sanitizeDecimal(value);
                    }}
                  />
                  <button type="button" class="btn btn-accent mt-3 h-10 w-full rounded-xl" onClick={submitValue}>
                    Confirm
                  </button>
                </div>
              </Teleport>
            ) : null}
          </div>
        );
      }

      return (
        <div class="relative w-full">
          <div ref={referenceEl} class="relative w-full">
            <button
              type="button"
              class={`l-box-shadow flex w-full items-center rounded-xl border text-left transition-all btn btn-xs lg:btn-md gap-0 ${
                props.isSmall ? " justify-center " : " justify-between "
              } ${
                isOpen.value || (props.isSmall && displayValue.value) ? "btn-accent" : ""
              } disabled:cursor-not-allowed disabled:opacity-40 ${props.class || ""}`}
              onClick={isOpen.value ? close : open}
              disabled={props.disabled}
              aria-label={props.placeholder}
            >
              {props.isSmall ? (
                <>
                  <span class={displayValue.value ? "text-sm font-semibold " : ""}>{displayValue.value || ""}</span>
                  <span class="material-symbols-outlined text-[18px] ">dialpad</span>
                </>
              ) : (
                <>
                  <span class={displayValue.value ? "text-sm font-semibold " : "text-sm "}>
                    {displayValue.value || props.placeholder}
                  </span>
                  <span class="flex items-center gap-2 ">
                    <span class="material-symbols-outlined text-[18px]">dialpad</span>
                    <span class="material-symbols-outlined text-[18px]">
                      {isOpen.value ? "expand_less" : "expand_more"}
                    </span>
                  </span>
                </>
              )}
            </button>
          </div>

          {isOpen.value && shouldUseModal.value ? (
            <LemmonModal title={props.title || ""} onClose={close}>
              <div class="space-y-4 p-4">
                <div></div>
                <div class="mx-auto w-full max-w-[400px]">
                  <NumPad
                    value={draftValue.value}
                    onChange={(value) => {
                      draftValue.value = sanitizeDecimal(value);
                    }}
                  />
                </div>
                <button type="button" class="btn btn-accent h-11 w-full rounded-xl" onClick={submitValue}>
                  Use value
                </button>
              </div>
            </LemmonModal>
          ) : null}

          {isOpen.value && !shouldUseModal.value ? (
            <Teleport to="body">
              <div
                ref={floatingEl}
                style={floatingStyles.value}
                class="z-[1200] w-[320px] max-w-[calc(100vw-24px)] overflow-hidden rounded-[28px] border border-base-content/10 bg-base-100/96 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur"
              >
                <div class="mb-3 flex items-center justify-between">
                  <div>
                    <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-base-content/45">
                      Guest Count
                    </div>
                    <div class="text-sm font-semibold text-base-content">Tap numbers to enter a custom size</div>
                  </div>
                  <button
                    type="button"
                    class="flex h-8 w-8 items-center justify-center rounded-full border border-base-content/10 text-base-content/55 transition-colors hover:border-base-content/20 hover:text-base-content"
                    onClick={close}
                  >
                    <span class="material-symbols-outlined text-[18px]">close</span>
                  </button>
                </div>
                <NumPad
                  value={draftValue.value}
                  onChange={(value) => {
                    draftValue.value = sanitizeDecimal(value);
                  }}
                />
                <button type="button" class="btn btn-accent mt-4 h-11 w-full rounded-xl" onClick={submitValue}>
                  Use value
                </button>
              </div>
            </Teleport>
          ) : null}
        </div>
      );
    };
  },
});
